import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Boot React. Every failure mode below is observable to the user
// either via the SentryErrorBoundary fallback (render-time errors)
// or the index.html watchdog (synchronous throws + silent mounts).
// We never let an exception here disappear into a blank page.
const rootEl = document.getElementById("root");

function showBootFailure(reason) {
  try {
    document.title = "Crystal Dispatch — Loading failed";
    const target = rootEl || document.body;
    target.innerHTML =
      '<div style="min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:32px;text-align:center;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;background:#fafafa;color:#0A0A0A">' +
        '<h1 style="font-size:24px;font-weight:900;margin:0 0 12px 0;letter-spacing:-0.02em">Couldn&rsquo;t load Crystal Dispatch</h1>' +
        '<p style="font-size:14px;color:#525252;max-width:380px;margin:0 0 24px 0;line-height:1.5">The app hit a startup error. Tap Reload to try again.</p>' +
        '<button onclick="location.reload()" style="background:#0A0A0A;color:#fff;font-weight:700;font-size:13px;letter-spacing:0.05em;text-transform:uppercase;padding:14px 28px;border:none;border-radius:0;cursor:pointer">Reload</button>' +
        '<p style="font-size:10px;color:#d4d4d4;margin:24px 0 0 0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;word-break:break-all;max-width:340px">' +
          String(reason || "unknown").replace(/[<>]/g, "") +
        '</p>' +
      '</div>';
  } catch {
    /* never let the recovery code itself break */
  }
}

if (!rootEl) {
  // index.html guarantees a #root div, but if some Cloudflare worker
  // ever strips it we should still tell the user instead of staying blank.
  showBootFailure("Missing #root element in HTML");
} else {
  let root;
  try {
    root = ReactDOM.createRoot(rootEl);
  } catch (e) {
    showBootFailure("createRoot: " + (e && e.message ? e.message : e));
  }

  if (root) {
    try {
      root.render(
        <React.StrictMode>
          <App />
        </React.StrictMode>,
      );
    } catch (e) {
      // React 18 commits asynchronously, so most render errors land in
      // the SentryErrorBoundary instead — but a synchronous throw here
      // (e.g. broken React.StrictMode in legacy Safari) would otherwise
      // silently blank the page. Surface it immediately, not after 10s.
      showBootFailure("render: " + (e && e.message ? e.message : e));
    }

    // Watchdog clear — polling only, never eager. Runs ONLY after React
    // commits something into #root. If the commit never happens (silent
    // boundary escape on iOS Safari), the inline watchdog in index.html
    // fires at T+10s and replaces the blank page with a recovery panel.
    if (typeof window !== "undefined" && window.__appBootWatchdog) {
      const startedAt = Date.now();
      const checkMounted = () => {
        // Stop conditions:
        //   1. React committed at least one child   → clear & exit
        //   2. Watchdog already fired (set to null) → exit
        //   3. >30s elapsed                          → exit (give up polling)
        if (!window.__appBootWatchdog) return;
        if (rootEl.children.length > 0) {
          clearTimeout(window.__appBootWatchdog);
          window.__appBootWatchdog = null;
          return;
        }
        if (Date.now() - startedAt > 30000) return;
        setTimeout(checkMounted, 200);
      };
      setTimeout(checkMounted, 50);
    }
  }
}
